/**
 * density.ts
 * Density type and per-density dimension constants for the Skill Matrix grid.
 *
 * Density controls row height and badge/quick-set button size.
 * The localStorage key is shared here so that SkillMatrixPage and any
 * future consumer read/write the same key.
 */

/** Three-tier density setting. */
export type Density = 'compact' | 'cozy' | 'comfortable';

/** localStorage key for persisting the user's density preference. */
export const DENSITY_LS_KEY = 'skillMatrix.density';

/** Default density on first visit. */
export const DEFAULT_DENSITY: Density = 'compact';

/**
 * Row height in pixels per density tier.
 * (Actual cell padding is managed by MUI TableCell; these values set the
 * explicit cell height on data rows.)
 */
export const ROW_HEIGHT_BY_DENSITY: Record<Density, number> = {
  compact: 28,
  cozy: 36,
  comfortable: 44,
};

/**
 * Skill badge (SkillLevelChip) pixel size per density tier.
 * Always 4px less than the row height so there is 2px padding each side.
 */
export const BADGE_SIZE_BY_DENSITY: Record<Density, number> = {
  compact: 22,
  cozy: 28,
  comfortable: 34,
};

/**
 * Employee avatar circle diameter per density tier.
 * Sized to fit comfortably inside the row height with 2px clearance each side.
 */
export const AVATAR_SIZE_BY_DENSITY: Record<Density, number> = {
  compact: 24,
  cozy: 30,
  comfortable: 36,
};

/** Read density from localStorage, falling back to the default. */
export function readDensity(): Density {
  try {
    const stored = window.localStorage.getItem(DENSITY_LS_KEY);
    if (stored === 'compact' || stored === 'cozy' || stored === 'comfortable') {
      return stored;
    }
  } catch {
    // localStorage blocked in private mode — silently fall back.
  }
  return DEFAULT_DENSITY;
}

/** Persist density to localStorage (best-effort). */
export function writeDensity(density: Density): void {
  try {
    window.localStorage.setItem(DENSITY_LS_KEY, density);
  } catch {
    // Ignore write errors.
  }
}
